import { z } from 'zod'
import type { Hex } from '../../types'
import { bigintTextSchema, lowerHexSchema } from '../../types/primitive'
import type { TransactionIndex } from '../../types/transactionIndex'
import type { BlockscoutClient } from './client'

type GetTransactionsByAddressParameters = {
  client: BlockscoutClient
  address: Hex
  startblock?: number
  endblock?: number
  page?: number
  offset?: number
  sort?: 'asc' | 'desc'
  headers?: Record<string, string>
}

const transactionSchema = z.object({
  blockNumber: z.string().regex(/^\d+$/).transform(Number),
  hash: lowerHexSchema,
  timeStamp: z.string().regex(/^\d+$/).transform(Number),
  input: lowerHexSchema,
  value: bigintTextSchema,
})
const resultSchema = z.array(transactionSchema)

/** {@link https://docs.blockscout.com/developer-support/api/rpc-endpoints/account#get-transactions-by-address} */
export const getTransactionsByAddress = async ({
  client,
  address,
  startblock = 0,
  endblock,
  page = 1,
  offset = 10_000,
  sort = 'asc',
  headers,
}: GetTransactionsByAddressParameters): Promise<TransactionIndex[]> => {
  try {
    const result = await client.get({
      path: '',
      query: {
        module: 'account',
        action: 'txlist',
        address,
        startblock: startblock.toString(),
        ...(endblock === undefined
          ? {}
          : {
              endblock: endblock.toString(),
            }),
        page: page.toString(),
        offset: offset.toString(),
        sort,
      },
      resultSchema,
      headers,
    })
    return result.map((v) => ({
      blockNumber: v.blockNumber,
      hash: v.hash,
      timestamp: v.timeStamp * 1000,
      input: v.input,
      value: v.value,
    }))
  } catch (error) {
    if (error instanceof Error && error.message === 'No transactions found') {
      return []
    }
    throw error
  }
}
